import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent, Button, Text } from "@musa.tech/design-system";
import { useFilters } from "@/hooks/useFilters";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { useOnDemandRequestPermissions } from "@/hooks/useOnDemandRequestPermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ProximasColetasTab } from "@/components/collects/proximas/ProximasColetasTab";
import { HistoricoColetasTab } from "@/components/collects/historico/HistoricoColetasTab";
import { SolicitacoesSobDemandaAgreements } from "@/components/collects/sob-demanda/agreements/SolicitacoesSobDemandaAgreements";

type ColetasTab = "proximas" | "historico" | "sobdemanda";

const VALID_TABS: ColetasTab[] = ["proximas", "historico", "sobdemanda"];

/** Cada aba tem a sua permissão — GR-637. A ordem é a de exibição. */
const TAB_PERMISSION: Record<ColetasTab, string> = {
  proximas: PERMISSIONS.VIEW_UPCOMING_COLLECTS,
  historico: PERMISSIONS.VIEW_COLLECTS_HISTORY,
  sobdemanda: PERMISSIONS.VIEW_ON_DEMAND_COLLECTS,
};

const TAB_HEADINGS: Record<ColetasTab, { title: string; subtitle: string }> = {
  proximas: {
    title: "Coletas",
    subtitle:
      "Acompanhe suas coletas, visualize o status da operação e acesse documentos como MTR e comprovantes.",
  },
  historico: {
    title: "Coletas",
    subtitle:
      "Acompanhe suas coletas, visualize o status da operação e acesse documentos como MTR e comprovantes.",
  },
  sobdemanda: {
    title: "Solicitações Sob Demanda",
    subtitle:
      "Acompanhe e gerencie as solicitações sob demanda dos seus estabelecimentos.",
  },
};

const Coletas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { trackButtonClick } = useAnalytics();
  const { hasBaselinePermission } = usePermissions();
  const { canRequestAny } = useOnDemandRequestPermissions();
  // A rota já garante que a pessoa tem ao menos UMA das três permissões
  // (PermissionRoute em App.tsx); aqui se decide QUAL aba.
  // `hasBaselinePermission`: as três abas são do grupo `default`, então quem
  // ainda não foi migrado precisa continuar vendo todas — com `hasPermission`
  // ele ficaria numa página sem aba nenhuma.
  const allowedTabs = VALID_TABS.filter((tab) =>
    hasBaselinePermission(TAB_PERMISSION[tab]),
  );

  const tabParam = searchParams.get("tab") as ColetasTab | null;
  const deepLinkValido =
    tabParam && VALID_TABS.includes(tabParam) && allowedTabs.includes(tabParam);

  // Fallback é a PRIMEIRA aba permitida, não "proximas" fixo: quem só tem
  // Histórico entrava numa aba vazia e sem explicação. Deep link para uma aba
  // sem permissão cai no mesmo lugar, em vez de mostrar uma aba proibida.
  const activeTab: ColetasTab =
    (deepLinkValido ? tabParam : allowedTabs[0]) ?? "proximas";

  useEffect(() => {
    document.title = "Coletas | MUSA";
  }, []);

  const {
    filters,
    updateDateRange,
    updateMaterials,
    updateStates,
    updateEstablishments,
    updateGenerators,
    updateHasMtr,
    clearFilters,
    hasActiveFilters,
  } = useFilters();

  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  const filterHandlers = {
    filters,
    onDateRangeChange: updateDateRange,
    onMaterialsChange: updateMaterials,
    onStatesChange: updateStates,
    onEstablishmentsChange: updateEstablishments,
    onGeneratorsChange: updateGenerators,
    onHasMtrChange: updateHasMtr,
    onClearFilters: clearFilters,
    hasActiveFilters,
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 min-w-0">
        <div className="mb-2">
          <Text as="h1" variant="heading" size="large" className="text-2xl md:text-3xl font-bold text-foreground">
            {TAB_HEADINGS[activeTab].title}
          </Text>
          <Text as="p" variant="body" className="text-muted-foreground mt-2 text-sm md:text-base">
            {TAB_HEADINGS[activeTab].subtitle}
          </Text>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="max-w-full overflow-x-auto">
              <TabsList>
                {allowedTabs.includes("proximas") && (
                  <TabsTrigger value="proximas">Próximas Coletas</TabsTrigger>
                )}
                {allowedTabs.includes("historico") && (
                  <TabsTrigger value="historico">Histórico de Coletas</TabsTrigger>
                )}
                {allowedTabs.includes("sobdemanda") && (
                  <TabsTrigger value="sobdemanda">
                    Solicitações Sob Demanda
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            {activeTab === "sobdemanda" && (
              <div className="flex items-center gap-2 flex-wrap">
                {/* Ver solicitações e criar uma são permissões distintas: o
                    botão fica no cabeçalho do Tabs, fora do TabsContent, então
                    travar o corpo da aba não o esconderia.

                    Os DOIS caminhos abrem por aqui — a pré-seleção leva à
                    coleta prevista em contrato (`scheduled`) e "Continuar sem
                    pré-seleção" ao add-on (`extra`). Exigir só `scheduled`
                    esconderia a entrada de quem tem apenas o add-on, que a
                    rota já deixa passar. */}
                {canRequestAny && (
                <Button
                  size="sm"
                  data-testid="sobdemanda-nova-solicitacao"
                  onClick={() => {
                    trackButtonClick({
                      buttonLabel: 'sobdemanda_coletas_nova_solicitacao',
                    });
                    // na lista de movimentações de equipamento, a nova solicitação já abre nelas
                    navigate(
                      searchParams.get("servico") === "cacamba"
                        ? '/coleta-sob-demanda/nova?servico=cacamba'
                        : '/coleta-sob-demanda/nova',
                    );
                  }}
                  className="h-9"
                >
                  <Plus className="h-4 w-4 mr-2" /> Nova solicitação sob demanda
                </Button>
                )}
              </div>
            )}
          </div>

          <TabsContent value="proximas" className="mt-4 md:mt-6">
            <ProximasColetasTab {...filterHandlers} />
          </TabsContent>

          <TabsContent value="historico" className="mt-4 md:mt-6">
            <HistoricoColetasTab {...filterHandlers} />
          </TabsContent>

          <TabsContent value="sobdemanda" className="mt-4 md:mt-6">
            <SolicitacoesSobDemandaAgreements />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Coletas;
